<template>
<DxForm ref="newFieldForm" :form-data="newFieldObj" label-mode="floating" @fieldDataChanged="onNewFieldDataChanged">
	<DxSimpleItem>
		<div style="text-align:center;font-size:1.2em;font-weight:bold;margin:1em;border-bottom:1px solid #ccc;padding-bottom:0.5em;">Create new field</div>	
		<div style="text-align:center;margin-bottom:1em;">Choose what type of data is stored, and give it a label.</div>
	</DxSimpleItem>
	<DxSimpleItem css-class="disable-form-paddings" data-field="fieldtype" :label="{ text: 'Type' }" :is-required="true" help-text="This cannot be changed later." editor-type="dxSelectBox"
		:editor-options="{ valueExpr: 'id', displayExpr: 'text', dataSource: fieldTypes.filter(t => !t.internal || t.id == 'relationship') }" />
	<DxSimpleItem :visible="!!newFieldObj.fieldtype" css-class="reduce-form-paddings">
		<div style="padding: 10px;border: 1px solid rgb(204, 204, 204);box-shadow: rgba(0, 0, 0, 0.1) 0 5px 2px;display:flex;gap:10px;align-items:flex-start;">
			<div><i style="font-size:2em;" :class="'dx-icon-' + fieldTypeMeta(newFieldObj.fieldtype).icon"></i></div>
			<div style="color:#333;">{{ fieldTypeMeta(newFieldObj.fieldtype).hint }}</div>
		</div>
	</DxSimpleItem>

	<template v-if="newFieldObj.fieldtype != 'relationship'">
		<DxSimpleItem css-class="reduce-form-paddings" data-field="label" :label="{ text: 'Label' }" :is-required="true" help-text="Display name for this field." />
		<DxSimpleItem css-class="reduce-form-paddings" data-field="identifier" :label="{ text: 'Identifier' }" :is-required="true" help-text="Internal unique name for this field - cannot be changed later. Use lowercase letters, numbers and underscores only." />
		<DxSimpleItem>
			<DxButton text="Create Field" styling-mode="filled" type="default" @click="createNewField" width="100%" />
		</DxSimpleItem>
	</template>
	<DxSimpleItem v-else>
		<SchemaEditor :schema="{}" :isNewRelation="true" :relationStartId="schemaId" @setBusy="emit('setBusy', $event)" @cancel="emit('cancel')" @edited="emit('created', $event)" />
	</DxSimpleItem>
</DxForm>
</template>

<script setup>
/* eslint-disable */
// The field creator handles simply creating new fields within a schema. However, it has an alternate use of showing
// the schema editor if the user tries to create a 'relationship' field.
import { ref, nextTick, defineProps, inject } from 'vue';
import slugify from 'slugify';
import { DxForm, DxSimpleItem } from 'devextreme-vue/form';
import { DxButton } from 'devextreme-vue/button';
import { fieldTypes, fieldTypeMeta } from '../fields.js';
import { alert as dxAlert } from 'devextreme/ui/dialog';
import SchemaEditor from './schema-editor.vue';

const $http = inject('$http');
const newFieldForm = ref(null);

// We need the schema ID for our API call, and a callback to set the busy spinner in our parent.
const props = defineProps([
	'schemaId',
]);
const emit = defineEmits(['setBusy', 'created', 'cancel' ]);

// Object to hold temporary values for creating a new field.
const newFieldObj = ref({
	fieldtype: '',
	label: '',
	identifier: '',
});

// When creating a new field, automatically generate unique identifiers based on the label.
// But make sure we don't trigger the change event if we are automatically changing it.
let disableAutoChanges = false;
let insideAutoChange = false;
function onNewFieldDataChanged({ dataField, value }) {
	if (insideAutoChange)
		return;
	if (dataField === 'label') {
		updateDependentFields(value);
	} else if (dataField === 'identifier') {
		disableAutoChanges = true;
	}
}
function updateDependentFields(title) {
	const shouldUpdateIdentifier = !disableAutoChanges || !newFieldObj.value.identifier;
	if (shouldUpdateIdentifier) {
		insideAutoChange = true;
		newFieldObj.value.identifier = slugify(title, { replacement: '_', lower: true, strict: true, trim: true });
		nextTick(() => insideAutoChange = false);
	}
}

// Event handler for new field button clicked: create a new field within our schema immediately.
const createNewField = async () => {
	const validationResult = await newFieldForm.value.instance.validate();
	if (!validationResult.isValid) {
		return;
	}
	
	emit('setBusy', true);
	$http.post('/field', {
		schema_id: props.schemaId,
		field: newFieldObj.value.identifier,
		fieldtype: newFieldObj.value.fieldtype,
		label: newFieldObj.value.label,
		spec: [],
	}).then((result) => {
		emit('setBusy', false);
		emit('created', result.data);
	}).catch(error => {
		console.error(error);
		emit('setBusy', false);
		dxAlert(`<div class="dx-error-message"><i class="dx-icon-warning"></i><div class="dx-error-message-text">${error.response?.data?.message || 'Failed to create field'}</div></div>`, `Error ${error.response?.status || '0'} / ${error.response?.statusText || 'Unknown'}`);
	});
}

</script>
